import {CommonModule} from '@angular/common';
import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  OverigeBezitting,
  OverigeBezittingSoort
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiIconModule} from '@snelstart/ui-icon';

type OverigeBezittingFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  soort: FormControl<string | null>;
  nummer: FormControl<string | null>;
  directRendement: FormControl<number | null>;
  toenames: FormControl<number | null>;
  afnames: FormControl<number | null>;
  waardeEindJaar: FormControl<number | null>;
  berekendRendement: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-box3-overige-bezittingen',
  templateUrl: './box3-overige-bezittingen.component.html',
  styleUrls: ['./box3-overige-bezittingen.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        SharedUiDetailsModule,
        CommonModule,
        SharedUiDialogModule,
        SharedUiIconModule
    ]
})
export class Box3OverigeBezittingenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  protected readonly OverigeBezittingSoort = OverigeBezittingSoort;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public jaar: any;
  public index = 0;
  public dialogVisible = false;
  public heeftOWR = false;

  public bezitSoort: SelectFormControlItem<string>[] = [
    {value: OverigeBezittingSoort.VORDERING, label: 'Vordering'},
    {value: OverigeBezittingSoort.CONTANT_GELD, label: 'Contant geld'},
    {value: OverigeBezittingSoort.CRYPTO, label: 'Cryptobezittingen'},
    {value: OverigeBezittingSoort.BOUWDEPOT, label: 'Bouwdepot'},
    {value: OverigeBezittingSoort.PREMIEDEPOT, label: 'Premiedepot lijfr.-/kap.verzekering'},
    {value: OverigeBezittingSoort.PERIODIEKE_UITKERING, label: 'Recht op periodieke uitkeringen'},
    // {value: OverigeBezittingSoort.SANCTIEWAARDE_NETTO_PENSIOEN, label: 'Netto pensioen'},
    {value: OverigeBezittingSoort.KAPITAALVERZEKERING, label: 'Niet-vrijgestelde kapitaalverzekering'},
    {value: OverigeBezittingSoort.OVERIG, label: 'Overig'}
  ];

  public formGroup = new FormGroup({
    box3OverigeBezittingen: new FormArray<OverigeBezittingFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(entity => {
      this.formGroup.reset({}, {emitEvent: false});
      this.updateForm(entity?.aangifte?.box3?.overigeBezittingen || []);
      this.heeftOWR = entity.aangifte?.box3?.berekenWerkelijkRendement ?? false;
      this.jaar = entity.year;
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractInputForCalculation = () => this.box3OverigeBezittingen.getRawValue().map(item => ({
      bedrag: item.bedrag,
    }));

    this.changeSubscriptions.add(
      this.box3OverigeBezittingen.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        map(() => extractInputForCalculation()),
        startWith(extractInputForCalculation()),
        pairwise(),
        filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  showDialog(index: number): void {
    this.index = index;
    this.dialogVisible = true;
  }

  get box3OverigeBezittingen(): FormArray<OverigeBezittingFormGroup> {
    return this.formGroup.controls.box3OverigeBezittingen;
  }

  addRij(): void {
    this.box3OverigeBezittingen.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.box3OverigeBezittingen.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
    this.subscribeToChanges();
  }

  createFormGroup(item?: OverigeBezitting): OverigeBezittingFormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
      soort: new FormControl<string | null>((item?.soort as string | null) ?? null),
      nummer: new FormControl<string | null>(item?.nummer ?? null),
      directRendement: new FormControl<number | null>(item?.directRendement ?? null),
      toenames: new FormControl<number | null>(item?.toenames ?? null),
      afnames: new FormControl<number | null>(item?.afnames ?? null),
      waardeEindJaar: new FormControl<number | null>(item?.waardeEindJaar ?? null),
      berekendRendement: new FormControl<number | null>(0),
    });
  }

  private updateForm(data: OverigeBezitting[]): void {
    const lijst = data || [];
    const formArray = this.box3OverigeBezittingen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    const items: OverigeBezitting[] = this.formGroup.getRawValue().box3OverigeBezittingen
      .filter(item => item.bedrag !== 0 && !(item.bedrag == null && item.omschrijving == null && item.soort == null))
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
        soort: (item.soort as OverigeBezittingSoort | null) ?? undefined,
        nummer: item.nummer ?? undefined,
        directRendement: item.directRendement ?? undefined,
        toenames: item.toenames ?? undefined,
        afnames: item.afnames ?? undefined,
        waardeEindJaar: item.waardeEindJaar ?? undefined
      }));

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          box3: {
            ...currentEntity.aangifte.box3,
            overigeBezittingen: items
          }
        }
      } as IncomeTaxEntity;
    });
  }


  getOWRDialogTitel(i: number): string {
    const omschrijving = this.box3OverigeBezittingen.controls[i]?.get('omschrijving')?.value;
    if (!omschrijving) {
      return 'Werkelijk rendement overig bezit';
    }
    return `Werkelijk rendement: ${omschrijving}`;
  }

  getBerekendeWerkelijkRendement(i: number): any {
    const control = this.box3OverigeBezittingen.at(i);
    const soort = control?.get('soort')?.value;
    const bedrag = Number(control?.get('bedrag')?.value) || 0;
    const directRendement = Number(control?.get('directRendement')?.value) || 0;
    const toenames = Number(control?.get('toenames')?.value) || 0;
    const afnames = Number(control?.get('afnames')?.value) || 0;
    const waardeEindJaar = Number(control?.get('waardeEindJaar')?.value) || 0;

    if (!control?.get('directRendement')?.value &&
      !control?.get('toenames')?.value &&
      !control?.get('waardeEindJaar')?.value &&
      !control?.get('afnames')?.value
    ) {
      return null;
    }

    if (soort === OverigeBezittingSoort.CONTANT_GELD) {
      return directRendement || null;
    }

    this.box3OverigeBezittingen.at(i).patchValue({berekendRendement: directRendement + waardeEindJaar - bedrag + afnames - toenames}, {emitEvent: false});
    return directRendement + waardeEindJaar - bedrag + afnames - toenames;
  }
}
